import { User } from '../types';

// Compute API base similar to other api helpers.
// In tests (vitest) Node's fetch requires an absolute URL. Prefer the
// configured VITE_API_URL when present; otherwise use an absolute
// `http://localhost/api` during tests or when no browser `window` exists.
const ORIGIN = (import.meta.env.VITE_API_URL || '').replace(/\/+$/, '');
const IS_VITEST = typeof process !== 'undefined' && !!process.env.VITEST;
const API_BASE = ORIGIN
  ? `${ORIGIN}/api`
  : (IS_VITEST || typeof window === 'undefined')
    ? 'http://localhost/api'
    : '/api';

function pickSessionToken(preferAdmin = false): string {
  if (typeof window === 'undefined') return '';
  const crmToken = localStorage.getItem('crm_token') || '';
  const ownerToken = localStorage.getItem('owner_token') || '';
  const adminToken = localStorage.getItem('dev_admin_token') || '';
  const adminRole = (localStorage.getItem('dev_admin_role') || '').toLowerCase();
  const isAdminRole = adminRole === 'superadmin' || adminRole === 'basicadmin';

  // Explicit admin preview (system admin panel) — admin token wins.
  if (preferAdmin && adminToken && isAdminRole) return adminToken;
  // CRM / card-owner sessions must win over a parallel system-admin session.
  if (crmToken) return crmToken;
  if (ownerToken) return ownerToken;
  if (adminToken && isAdminRole) return adminToken;
  return adminToken || '';
}

async function fetchMeWithToken(token: string): Promise<User | null> {
  if (!token) return null;
  const headers: Record<string, string> = { authorization: `Bearer ${token}` };
  const res = await fetch(`${API_BASE}/me`, { credentials: 'include', headers });
  if (!res.ok) return null;
  const data = await res.json();
  if ((data as any)?.authenticated === false) return null;
  return data as User;
}

/** Resolve the card profile for the active system admin session (dev_admin_token only). */
export async function getAdminSessionUser(): Promise<User | null> {
  if (IS_VITEST) return null;
  try {
    const token = pickSessionToken(true);
    return await fetchMeWithToken(token);
  } catch (e) {
    console.error('getAdminSessionUser error', e);
    return null;
  }
}

export async function getCurrentUser(): Promise<User | null> {
  // During unit tests we avoid network calls. Tests can seed localStorage
  // or rely on a null current user.
  if (IS_VITEST) return null;
  try {
    const token = pickSessionToken(false);
    if (token) return await fetchMeWithToken(token);
    // Dev fallback: if no token, include role header for admin UI in development
    if (typeof window !== 'undefined') {
      const role = localStorage.getItem('dev_admin_role') || '';
      if (role) {
        const res = await fetch(`${API_BASE}/me`, {
          credentials: 'include',
          headers: { 'x-user-role': role },
        });
        if (!res.ok) return null;
        const data = await res.json();
        if ((data as any)?.authenticated === false) return null;
        return data as User;
      }
    }
    return null;
  } catch (e) {
    console.error('getCurrentUser error', e);
    return null;
  }
}

// Request an email OTP for 2FA; returns ok and optional cooldown (no demo code returned)
export async function requestEmailOtp(email: string, purpose: string = 'admin_login'): Promise<{ ok: boolean; cooldown?: number; sent?: boolean }>
{
  const res = await fetch(`${API_BASE}/auth/request-otp`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, purpose }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, sent: false, ...(data || {}) } as any;
  }
  const data = await res.json().catch(() => ({}));
  return { ok: !!data.ok, cooldown: data.cooldown, sent: data.sent !== false };
}

export async function verifyEmailOtp(email: string, code: string, purpose: string = 'admin_login'): Promise<{ ok: boolean; proof?: string }>
{
  const res = await fetch(`${API_BASE}/auth/verify-otp`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, code, purpose }),
  });
  if (!res.ok) return { ok: false };
  const data = await res.json().catch(() => ({}));
  return { ok: !!data.ok, proof: data.proof };
}
